import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { 
  TrendingUp, 
  Video, 
  Users, 
  ChevronRight, 
  ChevronLeft,
  Wallet,
  CheckCircle2,
  Sparkles
} from "lucide-react";
import { useWallet } from "@/contexts/WalletContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface UserOnboardingProps {
  open: boolean;
  onComplete: () => void;
}

type UserType = "trader" | "streamer" | "community" | null;

const userTypes = [
  {
    id: "trader" as const,
    title: "Trader",
    description: "Trade tokens, track portfolios, and follow whale movements",
    icon: TrendingUp,
    features: ["Quick Buy/Sell", "Portfolio Tracking", "Price Alerts", "Copy Trading"],
  },
  {
    id: "streamer" as const,
    title: "Streamer",
    description: "Stream trades live, earn from your calls, and build an audience",
    icon: Video,
    features: ["OBS Overlay", "Trade Alerts", "Vote Multiplier", "Audience Rewards"],
  },
  {
    id: "community" as const,
    title: "Community Leader",
    description: "Create a community, coordinate investments, and earn together",
    icon: Users,
    features: ["Community Creation", "Group Trades", "Leaderboards", "Treasury Management"],
  },
];

export function UserOnboarding({ open, onComplete }: UserOnboardingProps) {
  const { connected, publicKey, connect } = useWallet();
  const [step, setStep] = useState(1);
  const [userType, setUserType] = useState<UserType>(null);
  const [username, setUsername] = useState("");
  const [discordHandle, setDiscordHandle] = useState("");
  const [twitterHandle, setTwitterHandle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = 3;

  const handleNext = () => {
    if (step === 1 && !connected) {
      toast({ title: "Please connect your wallet first", variant: "destructive" });
      return;
    }
    if (step === 2 && !userType) {
      toast({ title: "Please select how you'll use the platform", variant: "destructive" });
      return;
    }
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    if (!publicKey) return;

    setIsSubmitting(true);
    try {
      // Save profile to database
      const { error } = await supabase.from("profiles").upsert({
        wallet_address: publicKey,
        username: username || null,
        socials: {
          discord: discordHandle || null,
          twitter: twitterHandle || null,
        },
      });

      if (error) throw error;

      // Also create/update user record
      await supabase.from("users").upsert({
        wallet_address: publicKey,
        username: username || null,
        is_streamer: userType === "streamer",
      });

      // Store onboarding completion in localStorage
      localStorage.setItem("onboarding_complete", "true");
      localStorage.setItem("user_type", userType || "trader");

      toast({ title: "Welcome to TheMoonRun!", description: "Your profile has been set up." });
      onComplete();
    } catch (error) {
      console.error("Onboarding error:", error);
      toast({ title: "Failed to save profile", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden" hideCloseButton>
        {/* Progress Bar */}
        <div className="h-1 bg-secondary">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>

        <div className="p-6">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-2xl font-display flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-primary" />
              Welcome to TheMoonRun
            </DialogTitle>
            <DialogDescription>
              Let's set up your account in just a few steps
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: Connect Wallet */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Wallet className="w-10 h-10 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Connect Your Wallet</h3>
                <p className="text-muted-foreground max-w-sm mx-auto">
                  Connect your Solana wallet to get started. This will be your identity on the platform.
                </p>
              </div>

              {connected ? (
                <div className="flex items-center justify-center gap-3 p-4 rounded-lg bg-success/10 border border-success/20">
                  <CheckCircle2 className="w-5 h-5 text-success" />
                  <span className="text-success font-medium">Wallet Connected</span>
                  <code className="text-xs text-muted-foreground">
                    {publicKey?.slice(0, 4)}...{publicKey?.slice(-4)}
                  </code>
                </div>
              ) : (
                <Button onClick={connect} className="w-full" size="lg">
                  <Wallet className="w-4 h-4 mr-2" />
                  Connect Wallet
                </Button>
              )}
            </div>
          )}

          {/* Step 2: Select User Type */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold mb-2">How will you use TheMoonRun?</h3>
                <p className="text-muted-foreground">
                  Choose your primary activity. You can always access all features.
                </p>
              </div>

              <div className="grid gap-3">
                {userTypes.map((type) => (
                  <Card
                    key={type.id}
                    className={cn(
                      "p-4 cursor-pointer transition-all hover:border-primary/50",
                      userType === type.id && "border-primary bg-primary/5"
                    )}
                    onClick={() => setUserType(type.id)}
                  >
                    <div className="flex items-start gap-4">
                      <div className={cn(
                        "w-12 h-12 rounded-lg flex items-center justify-center",
                        userType === type.id ? "bg-primary text-primary-foreground" : "bg-secondary"
                      )}>
                        <type.icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{type.title}</h4>
                          {userType === type.id && (
                            <CheckCircle2 className="w-4 h-4 text-primary" />
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{type.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {type.features.map((feature) => (
                            <Badge key={feature} variant="secondary" className="text-xs">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Profile Setup */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold mb-2">Set Up Your Profile</h3>
                <p className="text-muted-foreground">
                  Help others find and connect with you (all fields optional)
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Choose a unique username"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="discord">Discord Handle</Label>
                  <Input
                    id="discord"
                    value={discordHandle}
                    onChange={(e) => setDiscordHandle(e.target.value)}
                    placeholder="username#1234"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="twitter">Twitter / X Handle</Label>
                  <Input
                    id="twitter"
                    value={twitterHandle}
                    onChange={(e) => setTwitterHandle(e.target.value)}
                    placeholder="@username"
                  />
                </div>
              </div>

              <div className="p-4 rounded-lg bg-secondary/50 text-sm text-muted-foreground">
                <p>
                  <strong>Tip:</strong> Connecting your socials helps you join communities 
                  and enables features like Discord notifications for your watchlist.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t border-border">
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={step === 1}
              className={cn(step === 1 && "invisible")}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "w-2 h-2 rounded-full transition-colors",
                    i + 1 === step ? "bg-primary" : "bg-secondary"
                  )}
                />
              ))}
            </div>

            {step < totalSteps ? (
              <Button onClick={handleNext} disabled={step === 1 && !connected}>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={handleComplete} disabled={isSubmitting}>
                {isSubmitting ? "Setting up..." : "Get Started"}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
